/**
 * Agent detail — `docs/SCREENS.md` §6. The screen the whole product is judged on.
 *
 * Its claim is narrow and the interface must not overstate it. The chain shown here is a
 * PER-SUBJECT SLICE: the rows about this agent, pulled out of a chain that also contains everybody
 * else's. Each row's payload can be verified against its own hashes, and it is. The LINKS between
 * these particular rows cannot — they belong to the full chain — so a green tick here would claim
 * more than has been checked. The API says which claim it made and this screen prints it.
 *
 * That distinction is the difference between evidence and a reassuring badge, and it is exactly the
 * kind of thing an interface quietly rounds up.
 */

import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { TIER_RULES, missingRequiredThreats, type IdentityKind, type ThreatId } from "@hub/core/browser";
import { api, relative, remaining, useApi } from "../lib/api";
import { Avatar, CampaignGap, Card, DecisionChain, KindBadge, Pill, TierBadge, Toast, type ChainStep } from "../components";

interface Detail {
  identity: {
    id: string; kind: IdentityKind; displayName: string; status: string; lifecycleState: string;
    lastSeenAt: string | null;
    owner: { id: string; displayName: string } | null;
    agentProfile: {
      purpose: string; capabilityTier: 1 | 2 | 3; owningTeam: string;
      registeredTools: string[]; dataScopes: string[];
    } | null;
    credentials: { id: string; kind: string; issuedAt: string; expiresAt: string | null; revokedAt: string | null; purposeRef: string | null }[];
    reports: { version: number; capabilities: string[]; limitations: string[]; safeguards: string[]; approvedBy: string | null; approvedAt: string | null }[];
  };
  chain: { chainSeq: number; occurredAt: string; action: string; payload: Record<string, unknown>; hash: string }[];
  chainVerdict: { ok: boolean; firstBreak: { seq: number; kind: string; detail: string } | null; payloadsUnchecked: boolean; scope: string };
}

export function AgentDetail() {
  const { id } = useParams();
  const [toast, setToast] = useState<string | null>(null);
  const { data, loading, error, reload } = useApi<Detail>(id ? `/v1/identities/${id}` : null);

  if (loading) return <p className="empty">Loading…</p>;
  if (error) return <p className="err">{error.message}{error.hint ? ` — ${error.hint}` : ""}</p>;
  if (!data) return null;

  const { identity, chain, chainVerdict } = data;
  const profile = identity.agentProfile;
  const report = identity.reports[0];
  const liveCredential = identity.credentials.find(
    (c) => c.revokedAt === null && (!c.expiresAt || Date.parse(c.expiresAt) > Date.now()));

  const steps: ChainStep[] = chain.map((e) => ({
    at: new Date(e.occurredAt).toISOString().replace("T", " ").slice(0, 19) + " UTC",
    label: e.action,
    detail: describe(e.payload),
    hash: e.hash,
    tone: e.action.includes("refused") || e.action.includes("denied") ? "warn" : "ok",
  }));

  async function act(path: string, body: unknown, ok: string) {
    try {
      await api(path, { method: "POST", body: JSON.stringify(body) });
      setToast(ok);
      reload();
    } catch (e) {
      setToast(e instanceof Error ? e.message : "That did not work.");
    }
    setTimeout(() => setToast(null), 4500);
  }

  return (
    <>
      <div className="page-head">
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
          <Avatar kind={identity.kind} name={identity.displayName} />
          <div>
            <h1>{identity.displayName}</h1>
            <p className="sub" style={{ marginTop: 4 }}>
              {profile?.purpose ?? "A person in the directory."}
            </p>
            <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
              <KindBadge kind={identity.kind} />
              {profile && <TierBadge tier={profile.capabilityTier} meaning={TIER_RULES[profile.capabilityTier].meaning} />}
              <Pill tone={identity.status === "active" ? "ok" : "bad"}>{identity.status}</Pill>
            </div>
          </div>
        </div>
      </div>

      <div className="grid k2" style={{ marginBottom: 14 }}>
        <Card title="Facts">
          <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "8px 16px", margin: 0, fontSize: 13 }}>
            <dt style={{ color: "var(--muted)" }}>Accountable owner</dt>
            <dd style={{ margin: 0 }}>
              {identity.owner
                ? <Link to={`/identities/${identity.owner.id}`}>{identity.owner.displayName}</Link>
                : <Pill tone="bad">none</Pill>}
            </dd>
            <dt style={{ color: "var(--muted)" }}>Credential</dt>
            <dd style={{ margin: 0 }}>
              {liveCredential
                ? <>{liveCredential.kind} · {remaining(liveCredential.expiresAt)}</>
                : <span style={{ color: "var(--muted)" }}>none live</span>}
            </dd>
            <dt style={{ color: "var(--muted)" }}>Last issued</dt>
            <dd style={{ margin: 0 }}>
              {identity.credentials[0] ? relative(identity.credentials[0].issuedAt) : "never"}
            </dd>
            <dt style={{ color: "var(--muted)" }}>Data scope</dt>
            <dd style={{ margin: 0 }}>
              {profile
                ? profile.dataScopes.length === 0
                  ? <Pill tone="warn">none declared — starts with zero by least_privilege_default</Pill>
                  : `${profile.dataScopes.length} class(es)`
                : "—"}
            </dd>
            <dt style={{ color: "var(--muted)" }}>Owning team</dt>
            <dd style={{ margin: 0 }}>{profile?.owningTeam ?? "—"}</dd>
            <dt style={{ color: "var(--muted)" }}>Registered tools</dt>
            <dd style={{ margin: 0, color: "var(--muted)" }}>
              {profile?.registeredTools.join(", ") || "—"}
            </dd>
          </dl>
        </Card>

        <Card title="Capability & Safeguard Report"
              link={report ? <span>v{report.version}</span> : undefined}>
          {!report ? (
            <p className="sub" style={{ marginTop: 0 }}>
              No approved report. {profile?.capabilityTier === 3
                ? "This agent holds tier 3, so an irreversible action is blocked until a report is approved by somebody outside the owning team."
                : "One is required before promotion to tier 3."}
            </p>
          ) : (
            <div style={{ fontSize: 13 }}>
              <Group title="Can" items={report.capabilities} tone="ok" />
              <Group title="Cannot" items={report.limitations} tone="bad" />
              <Group title="Safeguards" items={report.safeguards} tone="warn" />
              <p style={{ color: "var(--muted)", fontSize: 12, marginBottom: 0 }}>
                {report.approvedAt
                  ? `Approved ${relative(report.approvedAt)}.`
                  : "Drafted, not yet approved."}
              </p>
            </div>
          )}
        </Card>
      </div>

      <div className="stack">
        <Card title="Signed decision chain">
          <DecisionChain steps={steps} verified={chain.length === 0 ? null : chainVerdict.ok} />
          {/* The scope of the claim, printed. An interface that shows a tick without it is claiming
              more than was checked. */}
          <p className="sub" style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
            {chainVerdict.scope}.{" "}
            {chainVerdict.firstBreak && (
              <strong style={{ color: "var(--bad)" }}>
                First break at {chainVerdict.firstBreak.seq}: {chainVerdict.firstBreak.detail}.
              </strong>
            )}
          </p>
        </Card>

        {profile && profile.capabilityTier >= 2 && <PromotionReadiness agentId={identity.id} />}

        {profile && (
          <Card title="Actions">
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button className="btn" disabled={!liveCredential}
                      onClick={() => liveCredential && act(
                        `/v1/credentials/${liveCredential.id}/revoke`, {},
                        "Revoked, and the vendor confirmed. The chain records both.")}>
                Revoke credential
              </button>
              <button className="btn" onClick={() => act("/v1/audit/note", {
                subjectType: "Identity", subjectId: identity.id,
                note: "Reviewed from the agent detail screen.",
              }, "Noted. A correction to history is a new event, never an edit.")}>
                Add a reviewer note
              </button>
              <Link className="btn" to={`/graph?focus=${identity.id}`}>Trace in access graph</Link>
            </div>
            <p className="sub" style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
              Rotation and tier-3 promotion route through the approval flow rather than happening
              from this screen — a promotion needs an approver outside {profile.owningTeam} who can
              refuse alone, and a button here would be a way around that.
            </p>
          </Card>
        )}
      </div>

      {toast && <Toast message={toast} kind={identity.kind} />}
    </>
  );
}

interface CampaignRow {
  id: string; name: string; status: string; outcome: string | null; independent: boolean;
  completedAt: string | null; threatsCovered: string[]; target: { id: string } | null;
}
interface Readiness {
  ready: boolean;
  blockers: { code: string; reason: string; remedy: string }[];
}

/**
 * Why a tier-3 promotion would or would not be refused, on the agent's own page.
 *
 * Nothing here is computed in this file that the server does not already decide: the required set
 * is the API's `requiredForTier3`, the gap is `missingRequiredThreats` (the function the gate
 * calls), and the refusal text is the readiness endpoint's own `blockers`. Where there is no
 * campaign the block says so rather than inventing a coverage figure.
 */
function PromotionReadiness({ agentId }: { agentId: string }) {
  const campaigns = useApi<{ campaigns: CampaignRow[]; requiredForTier3: ThreatId[] }>("/v1/adversarial/campaigns");
  const readiness = useApi<Readiness>(`/v1/agents/${agentId}/promotion-readiness?toTier=3`);

  if (campaigns.loading || readiness.loading) {
    return <Card title="Promotion readiness"><p className="empty">Loading…</p></Card>;
  }
  if (campaigns.error || !campaigns.data) {
    return <Card title="Promotion readiness"><p className="err">{campaigns.error?.message ?? "Unavailable."}</p></Card>;
  }
  const required = campaigns.data.requiredForTier3;
  // The campaigns the gate considers for this agent — its own, and estate-wide ones — completed,
  // newest first. The same selection readinessFor makes on the server.
  const latest = campaigns.data.campaigns
    .filter((c) => c.status === "complete" && (c.target === null || c.target.id === agentId))
    .sort((a, b) => Date.parse(b.completedAt ?? "0") - Date.parse(a.completedAt ?? "0"))[0];
  const missing = latest ? missingRequiredThreats(latest.threatsCovered, required) : [];

  return (
    <Card title="Promotion readiness" testId="promotion-readiness">
      <div style={{ fontSize: 13 }}>
        <p className="sub" data-testid="readiness-required" style={{ marginTop: 0, fontSize: 12.5 }}>
          Tier 3 needs one passing, current, independent campaign covering: {required.join(", ")}.
        </p>
        {!latest ? (
          <p data-testid="readiness-no-campaign" style={{ margin: 0 }}>
            No completed adversarial campaign has been recorded for this agent.
          </p>
        ) : (
          <>
            <div>
              Latest campaign: <strong>{latest.name}</strong>{" "}
              <span style={{ color: "var(--muted)" }}>
                ({latest.outcome ?? "no outcome"}{latest.completedAt ? `, completed ${relative(latest.completedAt)}` : ""})
              </span>
            </div>
            <div style={{ fontSize: 12, color: "var(--dim)", marginTop: 4 }}>
              covered: {latest.threatsCovered.join(", ")}
            </div>
            <CampaignGap missing={missing} />
          </>
        )}
        {readiness.data && !readiness.data.ready && (
          <ul data-testid="readiness-refusal" style={{ margin: "10px 0 0", paddingLeft: 18, color: "var(--muted)" }}>
            {readiness.data.blockers.map((b) => (
              <li key={b.code}>{b.reason} <span style={{ color: "var(--dim)" }}>{b.remedy}</span></li>
            ))}
          </ul>
        )}
        {readiness.data?.ready && (
          <p style={{ margin: "10px 0 0", color: "var(--muted)" }}>The gate would not refuse a tier-3 promotion today.</p>
        )}
      </div>
    </Card>
  );
}

function Group({ title, items, tone }: { title: string; items: string[]; tone: "ok" | "bad" | "warn" }) {
  if (items.length === 0) return null;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 12, color: `var(--${tone})`, fontWeight: 600, marginBottom: 4 }}>{title}</div>
      <ul style={{ margin: 0, paddingLeft: 18, color: "var(--muted)" }}>
        {items.map((i) => <li key={i}>{i}</li>)}
      </ul>
    </div>
  );
}

/**
 * A one-line summary of an audit payload.
 *
 * The fallback is the part worth noting. It used to print `_subject`, which renders as
 * "Identity:<uuid>" — the single fact the reader already has, since they are looking at that
 * identity's page. A step whose detail line says nothing is worse than one with no detail line,
 * because it occupies the space where the answer would have been. So the fallback now reports
 * whatever ordinary fields the event carried, and says plainly when there were none.
 */
function describe(payload: Record<string, unknown>): string {
  const interesting = ["reason", "decision", "verdict", "policy", "ttlSeconds", "expiresAt",
                       "outOfTierReason", "routedTo", "confirmed", "note", "capabilityTier",
                       "displayName", "agentsReassigned", "agentsRetired", "orphansRemaining",
                       "from", "to", "purposeRef", "dataScopes"];
  const parts = interesting
    .filter((k) => {
      const v = payload[k];
      return v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0);
    })
    .map((k) => {
      const v = payload[k];
      const label = k.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
      return `${label}: ${Array.isArray(v) ? (v as unknown[]).join(", ") : String(v)}`;
    });
  return parts.length > 0 ? parts.join(" · ") : "no further detail recorded";
}
